/**
 * The shape of one entry in a `locales/*.json` dictionary, shared by the runtime that READS those
 * files (`src/frontend/i18n.ts`) and the pipeline that WRITES them (`src/services/i18nTranslate.ts`)
 * - so a plural entry can never come to mean two different things at the two ends.
 *
 * An entry is either a plain string, or CLDR plural forms that `Intl.PluralRules` selects between.
 * `other` is required: every locale defines it, and it is the fallback when a locale asks for a
 * category the file doesn't carry.
 */

/**
 * The UI locales the app ships a dictionary for.
 *
 * ⚠ The ARRAY is the declaration and the union is DERIVED from it, which is round 178's rule
 * (`SETTINGS_TABS` → `SettingsTab`) and round 179's (`RELEASE_TABS` → `ReleaseTab`) in the lane
 * where it had gone unasked the longest. Until German, `"en" | "da"` was hand-written in NINE
 * places - the frontend's `UiLocale`, its `LocaleMode`, its `SUPPORTED` list, two of its runtime
 * checks, `serverI18n`'s `OwnerLocale` and `ownerLocale()`, the settings zod enum, two route
 * reads, the theme's mode coercion and the landing page's Accept-Language fork - and a
 * hand-written union beside a hand-written runtime list is only HALF checked: an element type
 * catches a list entry outside the union and says nothing about a union member the list omits.
 * Since the resolvers match a stored value against the LIST, that omission is a locale that
 * typechecks everywhere and silently falls back to English at runtime.
 *
 * One declaration cannot disagree with itself, so adding the next language is this line plus a
 * dictionary - which is the whole point of §E, and was the thing that made German cost a round.
 *
 * ⚠ This is NOT `TARGET_LOCALES` (shared/languages.ts) and the two must never be merged. That list
 * is the languages the app can PUBLISH a press kit and a fan page in - a fact about the artist's
 * audience, with an `epkStrings` table behind it. This one is the languages the app's own CHROME
 * is translated into - a fact about the owner sitting in the tool. German has been in the first
 * list for dozens of rounds while being unavailable in the second.
 */
export const UI_LOCALES = ["en", "da", "de"] as const;

/** A locale the app has a dictionary for. Derived - add a language to UI_LOCALES, not here. */
export type UiLocale = (typeof UI_LOCALES)[number];

/**
 * The stored choice: a pinned locale, or "system" = follow the browser.
 *
 * "system" stores NO row, which is what a fresh workspace should do, and is why a stored value has
 * three states rather than two (round 161's clock made the same distinction for the same reason).
 */
export type LocaleMode = "system" | UiLocale;

/** The stored modes as a runtime list, for the zod enum that validates the settings payload. */
export const LOCALE_MODES = ["system", ...UI_LOCALES] as const;

/**
 * The cookie holding the language this BROWSER's reader picked (2026-09-25): on the front door's
 * footer, or in Settings. Absent means "follow the browser's own language".
 *
 * ⚠ **It is read on BOTH sides, which is why the name lives here**: the server renders the front door
 * in it, and the app reads it when an account has stored no language of its own - so a reader who
 * picked English on the front door and pressed "Log ind", "Kom i gang" or "Se et eksempel" does not
 * land in Danish because their phone is Danish. `public/index.html` restates it as a literal (it can
 * import nothing) and `tests/i18n.test.ts` holds the two together. It grants nothing, so it is NOT
 * HttpOnly: the app has to read it, and Settings has to write it.
 */
export const LOCALE_PICK_COOKIE = "gh_lang";

/**
 * How far along a locale's dictionary is - and why a PARTIAL one is allowed to ship.
 *
 * ⚠ The thing that makes this safe is a decision made long before German: **every reader of a
 * dictionary falls back to English per KEY.** `t()` is `DICTS[locale][key] ?? DICTS.en[key] ?? key`
 * and `serverText` is the same, so a key the locale has not reached renders as the English sentence,
 * never as a blank and never as a raw key. A half-translated locale is therefore a MIXED UI, which
 * is a poorer experience than English and a much better one than no German at all - and it improves
 * by itself every time the fill tool runs, with no code change.
 *
 * That is also why `mergeLocale` refuses to write the English string into a locale file: the
 * fallback must stay a runtime fact rather than a copy, or nothing can tell an untranslated key from
 * a translated one that happens to read the same, and "what is left to do" becomes unanswerable.
 *
 * The status exists because the two kinds of locale need OPPOSITE gates:
 *
 * - **`complete`** - every key a backend module names must be present. A key going missing here is a
 *   regression, and `tests/i18n.test.ts` fails on it.
 * - **`in-progress`** - completeness is not asserted, because it is knowingly false. Everything
 *   about the keys that ARE there is still asserted exactly as hard: placeholder parity, entry kind,
 *   plural form coverage, the product name, no stale keys.
 *
 * ⚠ Without this split there were only two options and both were wrong: fail the suite forever, or
 * hold the language out of the app until a single run of a rate-limited tool happened to finish.
 * ⚠ And `in-progress` is a RATCHET, not an amnesty - a locale that has become complete must be
 * promoted, and the test fails until it is. An exemption that outlives its reason is the stale
 * disposition this repo keeps finding (round 153); here it would silently stop guarding a finished
 * language against regression.
 */
export const LOCALE_STATUS: Record<UiLocale, "source" | "complete" | "in-progress"> = {
	en: "source",
	da: "complete",
	// Round 187 filled it a batch at a time; the owner finished and reviewed the last of it on
	// 2026-08-29, so the ratchet above promoted it and the completeness gates guard it now.
	de: "complete"
};

/**
 * A language's name IN ITSELF, for the language picker.
 *
 * ⚠ Deliberately NOT `Intl.DisplayNames` and NOT `t()`. A picker is the one place a language name
 * must NOT be in the reader's language: someone who has landed in a UI they cannot read is looking
 * for the word they recognize, and "Tysk" helps nobody who only reads German. Same reasoning as
 * `shared/languages.ts`' `languageName` having a locale argument, with the argument pinned to the
 * entry itself. An endonym is also not ours to translate, so these are literals by design.
 */
export const LOCALE_ENDONYMS: Record<UiLocale, string> = {
	en: "English",
	da: "Dansk",
	de: "Deutsch"
};

/**
 * The `og:locale` spelling of each shipped language - `en_US`, not `en`.
 *
 * ⚠ **This is WIRE DATA, not chrome** (the three-kinds rule in `constants.ts`'s header): the reader
 * is Facebook's and LinkedIn's crawler, matching against THEIR vocabulary, which is an
 * underscore-joined language_TERRITORY tag and refuses a bare `en`. Translating it or shortening it
 * breaks its reader, so it is a table rather than something derived from the tag at the call site.
 *
 * ⚠ **A territory has to be CHOSEN and there is no correct answer to derive.** `en` is not `en_US`
 * any more than it is `en_GB`; the app's English is American (the standing rule that
 * `tests/americanEnglish.test.ts` gates), so `en_US` is the one that matches what the page says.
 * `Record<UiLocale, string>` is what makes the next language a type error rather than a page that
 * quietly claims to be in English.
 */
export const OG_LOCALES: Record<UiLocale, string> = {
	en: "en_US",
	da: "da_DK",
	de: "de_DE"
};

/** Narrow an untrusted string to a shipped locale - a stored row, a URL, an Accept-Language tag. */
export function isUiLocale(value: unknown): value is UiLocale {
	return typeof value === "string" && (UI_LOCALES as readonly string[]).includes(value);
}

/** Narrow an untrusted string to a stored locale MODE, "system" included. */
export function isLocaleMode(value: unknown): value is LocaleMode {
	return value === "system" || isUiLocale(value);
}

/**
 * The `{name}` placeholders a string carries, sorted and de-duplicated.
 *
 * ⚠ Shared so the fill pipeline and `tests/i18n.test.ts` cannot disagree about what a placeholder
 * IS. Two copies of this regex is the fork this repo keeps finding (round 166's streaming lists):
 * the gate would be asserting one thing while the tool that writes the files enforced another, and
 * the disagreement would show up as a dictionary that passes the suite and renders a literal
 * `{Anzahl}` - or the reverse, a pipeline rejecting good translations nothing else objects to.
 */
export function placeholdersOf(text: string): string[] {
	return [...new Set(text.match(/\{(\w+)\}/g) ?? [])].sort();
}

/** CLDR plural categories, in the order a locale file should list them */
export const PLURAL_CATEGORIES = ["zero", "one", "two", "few", "many", "other"] as const;

export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };
export type LocaleEntry = string | PluralForms;

export function isPluralForms(entry: unknown): entry is PluralForms {
	if (entry == null || typeof entry !== "object" || Array.isArray(entry)) return false;
	const keys = Object.keys(entry);
	return keys.length > 0 && keys.every(key => (PLURAL_CATEGORIES as readonly string[]).includes(key));
}

/**
 * Plural forms are carried through the fill pipeline as one flat key per form (`key#one`,
 * `key#other`) so the AI translator keeps its plain string-to-string contract and each form is
 * translated on its own. `#` because dictionary keys are dotted, so it cannot collide with one.
 *
 * Known limit: forms are flattened over the ENGLISH categories, so a target locale that needs more
 * of them than English does (Polish wants few/many) gets only one/other and must be hand-finished.
 * Both UI locales today (en, da) use exactly one/other.
 */
export const PLURAL_FORM_SEPARATOR = "#";
